import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { COMPANY_TYPES } from '../../constants/companyTypes';

const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

const currencyBRL = (cents = 0) =>
    (Number(cents || 0) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    });

const EditCompanyDialog = ({ open, onClose, company, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        cnpj: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        photo: null,
        planId: '',
        type: 'KNN',
    });
    const [error, setError] = useState(null);
    const [coordinates, setCoordinates] = useState(null);

    const [plans, setPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansError, setPlansError] = useState(null);

    useEffect(() => {
        if (company) {
            setFormData({
                name: company.name || '',
                cnpj: company.cnpj || '',
                street: company.address?.street || '',
                city: company.address?.city || '',
                state: company.address?.state || '',
                zipCode: company.address?.zipCode || '',
                photo: null,
                planId: company.currentPlan?.id
                    ? String(company.currentPlan.id)
                    : '',
                type: company.type || 'GAMEUP',
            });

            if (company.address?.latitude && company.address?.longitude) {
                setCoordinates({
                    lat: company.address.latitude,
                    lng: company.address.longitude,
                });
            } else {
                fetchCoordinatesFromAddress();
            }
        }
    }, [company]);

    useEffect(() => {
        if (!open) return;
        let isMounted = true;
        (async () => {
            setPlansLoading(true);
            setPlansError(null);
            try {
                const data = await RequestService.getPlansAsSuperAdmin({
                    page: 0,
                    size: 100,
                });
                const items = Array.isArray(data)
                    ? data
                    : (data?.content ?? []);
                if (isMounted) setPlans(items);
            } catch (e) {
                if (isMounted) setPlansError('Falha ao carregar planos.');
            } finally {
                if (isMounted) setPlansLoading(false);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, [open]);

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'file' ? files[0] : value,
        }));
    };

    const handleSubmit = async () => {
        try {
            const formDataToSend = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    formDataToSend.append(key, value);
                }
            });

            await RequestService.updateCompany(company.id, formDataToSend);
            onSuccess && onSuccess();
            onClose();
        } catch (err) {
            setError('Erro ao editar empresa. Verifique os dados preenchidos.');
        }
    };

    const fetchCoordinatesFromAddress = async () => {
        const { street, city, state, zipCode } = company.address || {};
        if (!street || !city || !state || !zipCode) return;

        const query = `${street}, ${city}, ${state}, ${zipCode}`;
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`;

        try {
            const response = await fetch(url);
            const data = await response.json();

            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                setCoordinates({ lat: parseFloat(lat), lng: parseFloat(lon) });
            }
        } catch (err) {
            console.error('Erro ao buscar coordenadas:', err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Editar Empresa
                <IconButton
                    aria-label='close'
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {!company ? (
                    <Alert severity='info'>Carregando dados...</Alert>
                ) : (
                    <Box display='flex' flexDirection='column' gap={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                                sx={{
                                    width: 100,
                                    height: 100,
                                    border: '1px dashed #CCCCCC',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                }}
                            >
                                {formData.photo ? (
                                    <img
                                        src={URL.createObjectURL(
                                            formData.photo
                                        )}
                                        alt='Preview'
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : company.photoUrl ? (
                                    <img
                                        src={company.photoUrl}
                                        alt='Preview'
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : (
                                    <AddPhotoAlternateOutlinedIcon
                                        fontSize='large'
                                        color='disabled'
                                    />
                                )}
                            </Box>
                        </Box>
                        <Button variant='contained' component='label' fullWidth>
                            Selecionar Imagem
                            <input
                                type='file'
                                accept='image/*'
                                hidden
                                name='photo'
                                onChange={handleChange}
                            />
                        </Button>

                        <TextField
                            label='Nome'
                            name='name'
                            value={formData.name}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label='CNPJ'
                            name='cnpj'
                            value={formData.cnpj}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label='Rua'
                            name='street'
                            value={formData.street}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label='Cidade'
                            name='city'
                            value={formData.city}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label='Estado'
                            name='state'
                            value={formData.state}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label='CEP'
                            name='zipCode'
                            value={formData.zipCode}
                            onChange={handleChange}
                            fullWidth
                        />

                        <FormControl fullWidth>
                            <InputLabel id='plan-select-label'>
                                Plano
                            </InputLabel>
                            <Select
                                labelId='plan-select-label'
                                label='Plano'
                                name='planId'
                                value={formData.planId}
                                onChange={handleChange}
                                disabled={plansLoading || !!plansError}
                            >
                                {plansLoading && (
                                    <MenuItem value='' disabled>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <CircularProgress size={18} />
                                            <Typography>
                                                Carregando planos...
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                )}
                                {plansError && (
                                    <MenuItem value='' disabled>
                                        {plansError}
                                    </MenuItem>
                                )}
                                {!plansLoading &&
                                    !plansError &&
                                    plans.length === 0 && (
                                        <MenuItem value='' disabled>
                                            Nenhum plano disponível
                                        </MenuItem>
                                    )}
                                {!plansLoading &&
                                    !plansError &&
                                    plans.map((p) => (
                                        <MenuItem
                                            key={p.id}
                                            value={String(p.id)}
                                        >
                                            {p.name} —{' '}
                                            {currencyBRL(p.priceCents)}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id='company-type-select-label'>
                                Tipo
                            </InputLabel>
                            <Select
                                labelId='company-type-select-label'
                                label='Tipo'
                                name='type'
                                value={formData.type}
                                onChange={handleChange}
                            >
                                {COMPANY_TYPES.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                        {t.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {coordinates && (
                            <Box
                                sx={{
                                    height: '250px',
                                    mt: 2,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                <MapContainer
                                    center={[coordinates.lat, coordinates.lng]}
                                    zoom={16}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
                                    <Marker
                                        position={[
                                            coordinates.lat,
                                            coordinates.lng,
                                        ]}
                                    >
                                        <Popup>
                                            {formData.street}, {formData.city} -{' '}
                                            {formData.state}
                                        </Popup>
                                    </Marker>
                                </MapContainer>
                            </Box>
                        )}

                        {error && <Alert severity='error'>{error}</Alert>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='outlined'>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant='contained'
                    color='primary'
                    disabled={!company}
                >
                    Salvar Alterações
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCompanyDialog;
